import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { useWallet } from '../context/WalletContext';

const LMLAirdrop = () => {
    const { miningContract, readOnlyMiningContract, account } = useWallet();
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [csvData, setCsvData] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [emptyOrderCsvData, setEmptyOrderCsvData] = useState([]);
    const [emptyOrderUploading, setEmptyOrderUploading] = useState(false);
    const emptyOrderFileInputRef = useRef(null);

    useEffect(() => {
        if (readOnlyMiningContract && account) {
            checkAdmin();
        }
    }, [readOnlyMiningContract, account]);

    const checkAdmin = async () => {
        try {
            setLoading(true);
            const adminRole = await readOnlyMiningContract.ADMIN_ROLE();
            const hasAdminRole = await readOnlyMiningContract.hasRole(adminRole, account);
            setIsAdmin(hasAdminRole);
        } catch (error) {
            console.error('Error checking admin status:', error);
        } finally {
            setLoading(false);
        }
    };

    const downloadSample = () => {
        const sampleData = '地址,金额\n0x1234567890123456789012345678901234567890,1000\n0xabcdefabcdefabcdefabcdefabcdefabcdefabcd,2000';
        const blob = new Blob([sampleData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = '特殊锁仓订单样例.csv';
        link.click();
    };

    const downloadEmptyOrderSample = () => {
        const sampleData = '地址,空单金额\n0x1234567890123456789012345678901234567890,500\n0xabcdefabcdefabcdefabcdefabcdefabcdefabcd,1000';
        const blob = new Blob([sampleData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = '空单设置样例.csv';
        link.click();
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split('\n').filter(line => line.trim());

                if (lines.length < 2) {
                    toast.error('CSV文件格式错误：至少需要标题行和一行数据');
                    return;
                }

                const data = [];
                const errors = [];

                for (let i = 1; i < lines.length; i++) {
                    const [address, amount] = lines[i].split(',').map(item => item.trim());

                    if (!address || !amount) {
                        errors.push(`第${i + 1}行：缺少地址或金额`);
                        continue;
                    }

                    if (!ethers.isAddress(address)) {
                        errors.push(`第${i + 1}行：无效的地址 ${address}`);
                        continue;
                    }

                    const parsedAmount = parseFloat(amount);
                    if (isNaN(parsedAmount) || parsedAmount <= 0) {
                        errors.push(`第${i + 1}行：无效的金额 ${amount}`);
                        continue;
                    }

                    data.push({ address, amount: parsedAmount });
                }

                if (errors.length > 0) {
                    toast.error(`发现 ${errors.length} 个错误：\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? '\n...' : ''}`);
                    return;
                }

                if (data.length === 0) {
                    toast.error('没有有效的数据');
                    return;
                }

                setCsvData(data);
                toast.success(`成功加载 ${data.length} 条记录`);
            } catch (error) {
                console.error('Error parsing CSV:', error);
                toast.error('CSV文件解析失败');
            }
        };
        reader.readAsText(file);
    };

    const handleEmptyOrderFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split('\n').filter(line => line.trim());

                if (lines.length < 2) {
                    toast.error('CSV文件格式错误：至少需要标题行和一行数据');
                    return;
                }

                const data = [];
                const errors = [];

                for (let i = 1; i < lines.length; i++) {
                    const [address, amount] = lines[i].split(',').map(item => item.trim());

                    if (!address || !amount) {
                        errors.push(`第${i + 1}行：缺少地址或金额`);
                        continue;
                    }

                    if (!ethers.isAddress(address)) {
                        errors.push(`第${i + 1}行：无效的地址 ${address}`);
                        continue;
                    }

                    const parsedAmount = parseFloat(amount);
                    if (isNaN(parsedAmount) || parsedAmount <= 0) {
                        errors.push(`第${i + 1}行：无效的金额 ${amount}`);
                        continue;
                    }

                    data.push({ address, amount: parsedAmount });
                }

                if (errors.length > 0) {
                    toast.error(`发现 ${errors.length} 个错误：\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? '\n...' : ''}`);
                    return;
                }

                if (data.length === 0) {
                    toast.error('没有有效的数据');
                    return;
                }

                setEmptyOrderCsvData(data);
                toast.success(`成功加载 ${data.length} 条记录`);
            } catch (error) {
                console.error('Error parsing CSV:', error);
                toast.error('CSV文件解析失败');
            }
        };
        reader.readAsText(file);
    };

    const handleUpload = async () => {
        if (csvData.length === 0) {
            toast.error('请先上传CSV文件');
            return;
        }

        if (!miningContract) {
            toast.error('合约未初始化');
            return;
        }

        setUploading(true);
        toast.loading('正在准备交易...', { id: 'upload' });

        try {
            const addresses = csvData.map(item => item.address);
            const amounts = csvData.map(item => ethers.parseEther(item.amount.toString()));

            const tx = await miningContract.batchSetLMLUsers(addresses, amounts);
            toast.loading('交易已提交，等待上链...', { id: 'upload' });

            const receipt = await tx.wait();

            if (receipt.status === 1) {
                toast.success('设置成功！', { id: 'upload' });
                setCsvData([]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                toast.error('交易执行失败', { id: 'upload' });
            }
        } catch (error) {
            console.error('Error:', error);
            let errorMsg = error.reason || error.message || '上传失败';
            if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
                errorMsg = '用户取消了交易';
            } else if (error.code === 'TRANSACTION_REPLACED') {
                if (error.cancelled) {
                    errorMsg = '交易已取消';
                } else {
                    toast.success('2设置成功！', { id: 'upload' });
                    setCsvData([]);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                    setUploading(false);
                    return;
                }
            } else if (error.message && error.message.includes("duplicate call detected")) {
                toast.success('设置成功！', { id: 'upload' });
                setCsvData([]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setUploading(false);
                return;
            }
            toast.error(errorMsg, { id: 'upload' });
        } finally {
            setUploading(false);
        }
    };

    const handleEmptyOrderUpload = async () => {
        if (emptyOrderCsvData.length === 0) {
            toast.error('请先上传CSV文件');
            return;
        }

        if (!miningContract) {
            toast.error('合约未初始化');
            return;
        }

        setEmptyOrderUploading(true);
        toast.loading('正在准备交易...', { id: 'emptyorder' });

        try {
            const addresses = emptyOrderCsvData.map(item => item.address);
            const amounts = emptyOrderCsvData.map(item => ethers.parseEther(item.amount.toString()));

            const tx = await miningContract.batchSetEmptyOrders(addresses, amounts);
            toast.loading('交易已提交，等待上链...', { id: 'emptyorder' });

            const receipt = await tx.wait();

            if (receipt.status === 1) {
                toast.success('空单批量设置成功！', { id: 'emptyorder' });
                setEmptyOrderCsvData([]);
                if (emptyOrderFileInputRef.current) {
                    emptyOrderFileInputRef.current.value = '';
                }
            } else {
                toast.error('交易执行失败', { id: 'emptyorder' });
            }
        } catch (error) {
            console.error('Error:', error);
            let errorMsg = error.reason || error.message || '上传失败';
            if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
                errorMsg = '用户取消了交易';
            } else if (error.code === 'TRANSACTION_REPLACED') {
                if (error.cancelled) {
                    errorMsg = '交易已取消';
                } else {
                    toast.success('空单批量设置成功！', { id: 'emptyorder' });
                    setEmptyOrderCsvData([]);
                    if (emptyOrderFileInputRef.current) {
                        emptyOrderFileInputRef.current.value = '';
                    }
                    setEmptyOrderUploading(false);
                    return;
                }
            } else if (error.message && error.message.includes("duplicate call detected")) {
                toast.success('空单批量设置成功！', { id: 'emptyorder' });
                setEmptyOrderCsvData([]);
                if (emptyOrderFileInputRef.current) {
                    emptyOrderFileInputRef.current.value = '';
                }
                setEmptyOrderUploading(false);
                return;
            }
            toast.error(errorMsg, { id: 'emptyorder' });
        } finally {
            setEmptyOrderUploading(false);
        }
    };

    if (loading) {
        return <div className="loading">加载中...</div>;
    }

    if (!isAdmin) {
        return (
            <div className="card">
                <h2 className="card-title">私募与空投</h2>
                <p style={ { color: '#ef4444', textAlign: 'center', padding: '20px' } }>
                    您没有管理员权限
                </p>
            </div>
        );
    }

    return (
        <div>
            <div className="card">
                <h2 className="card-title">批量设置锁仓订单</h2>
                <p style={ { fontSize: '14px', color: '#aaa', marginBottom: '16px' } }>
                    上传CSV文件批量设置用户的锁仓单金额
                </p>

                <div style={ { marginBottom: '16px' } }>
                    <button
                        className="btn"
                        style={ { background: '#555', marginBottom: '12px' } }
                        onClick={ downloadSample }
                    >
                        下载CSV样例文件
                    </button>
                    <p style={ { fontSize: '12px', color: '#888' } }>
                        CSV格式：地址,USDT金额（第一行为标题，从第二行开始为数据）
                    </p>
                </div>

                <div className="form-group">
                    <label className="form-label">选择CSV文件</label>
                    <input
                        ref={ fileInputRef }
                        type="file"
                        accept=".csv"
                        onChange={ handleFileUpload }
                        className="form-input"
                        style={ { padding: '8px' } }
                    />
                </div>

                { csvData.length > 0 && (
                    <div style={ { marginBottom: '16px' } }>
                        <p style={ { color: '#9acd32', marginBottom: '8px' } }>
                            已加载 { csvData.length } 条记录
                        </p>
                        <div style={ {
                            maxHeight: '200px',
                            overflow: 'auto',
                            background: '#1a1a1a',
                            padding: '12px',
                            borderRadius: '4px',
                            fontSize: '12px'
                        } }>
                            { csvData.slice(0, 10).map((item, index) => (
                                <div key={ index } style={ { marginBottom: '4px' } }>
                                    { item.address }: { item.amount } USDT
                                </div>
                            )) }
                            { csvData.length > 10 && (
                                <div style={ { color: '#888', marginTop: '8px' } }>
                                    ... 还有 { csvData.length - 10 } 条记录
                                </div>
                            ) }
                        </div>
                    </div>
                ) }

                <button
                    className="btn btn-primary"
                    onClick={ handleUpload }
                    disabled={ uploading || csvData.length === 0 }
                >
                    { uploading ? '上传中...' : '批量设置锁仓订单' }
                </button>
            </div>

            <div className="card">
                <h2 className="card-title">批量设置空单</h2>
                <p style={ { fontSize: '14px', color: '#aaa', marginBottom: '16px' } }>
                    上传CSV文件批量设置用户的空单金额
                </p>

                <div style={ { marginBottom: '16px' } }>
                    <button
                        className="btn"
                        style={ { background: '#555', marginBottom: '12px' } }
                        onClick={ downloadEmptyOrderSample }
                    >
                        下载CSV样例文件
                    </button>
                    <p style={ { fontSize: '12px', color: '#888' } }>
                        CSV格式：地址,空单金额（第一行为标题，从第二行开始为数据）
                    </p>
                </div>

                <div className="form-group">
                    <label className="form-label">选择CSV文件</label>
                    <input
                        ref={ emptyOrderFileInputRef }
                        type="file"
                        accept=".csv"
                        onChange={ handleEmptyOrderFileUpload }
                        className="form-input"
                        style={ { padding: '8px' } }
                    />
                </div>

                { emptyOrderCsvData.length > 0 && (
                    <div style={ { marginBottom: '16px' } }>
                        <p style={ { color: '#9acd32', marginBottom: '8px' } }>
                            已加载 { emptyOrderCsvData.length } 条记录
                        </p>
                        <div style={ {
                            maxHeight: '200px',
                            overflow: 'auto',
                            background: '#1a1a1a',
                            padding: '12px',
                            borderRadius: '4px',
                            fontSize: '12px'
                        } }>
                            { emptyOrderCsvData.slice(0, 10).map((item, index) => (
                                <div key={ index } style={ { marginBottom: '4px' } }>
                                    { item.address }: { item.amount } USDT
                                </div>
                            )) }
                            { emptyOrderCsvData.length > 10 && (
                                <div style={ { color: '#888', marginTop: '8px' } }>
                                    ... 还有 { emptyOrderCsvData.length - 10 } 条记录
                                </div>
                            ) }
                        </div>
                    </div>
                ) }

                <button
                    className="btn btn-primary"
                    onClick={ handleEmptyOrderUpload }
                    disabled={ emptyOrderUploading || emptyOrderCsvData.length === 0 }
                >
                    { emptyOrderUploading ? '上传中...' : '批量设置空单' }
                </button>
            </div>
        </div>
    );
};

export default LMLAirdrop;
